import styled from '@emotion/styled';
import { Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { omit } from 'lodash';
import React, { useState } from 'react';
import { ClientAnalyticsEventType } from '@/components/analytics/constants';
import { useTrackEventsAnalytics } from '@/components/analytics/hooks/use-track-events-analytics';
import { YellowButton } from '@/components/button/button';
import { CloseIcon } from '@/components/icons/icons';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
import {
  LatestThreadsDocument,
  useCheckIfThreadExistsMutation,
  useCreateNewThreadMutation,
  useSendMessageMutation,
} from '@/generated/graphql';
const MainDiv = styled(Dialog)`
  height: auto;
  width: 870;
  border-radius: 13.5205px;
  a {
    :hover {
      color: #5c91c6;
      text-decoration: none;
    }
  }
`;
const CloseButton = styled(IconButton)`
  position: absolute;
  right: 5px;
  top: 5px;
  color: black;
`;
const TitleDiv = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
`;
const ButtonDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;
export interface MessagePopUpProps {
  open: boolean;
  onClose: () => void;
  hostId: string;
  sentFrom?: ClientAnalyticsEventType;
}
const MessagePopUp = ({ open, onClose, hostId, sentFrom }: MessagePopUpProps) => {
  const [checkThreadMutation] = useCheckIfThreadExistsMutation();
  const [createNewMessageThreadMutation] = useCreateNewThreadMutation();
  const [createNewMessage] = useSendMessageMutation();
  const [message, setMessage] = useState<string>('');
  const { data: user } = useCurrentUser();
  const userId = user?.id;
  const { fireEvent } = useTrackEventsAnalytics();
  const handleSend = async () => {
    const res = await checkThreadMutation({
      variables: {
        otherUserId: hostId,
      },
    });

    const threadId = res.data?.checkIfThreadExists?.messageThreadId;
    if (!sentFrom) {
      return;
    }
    if (threadId !== undefined) {
      const messageRes = await createNewMessage({
        variables: {
          input: {
            message: {
              messageThread: { id: threadId as string },
              user: { id: userId! },
              content: message,
            },
          },
        },
        refetchQueries: [
          {
            query: LatestThreadsDocument,
            variables: { userId },
          },
        ],
      });
      const msgData = {
        ...messageRes?.data?.createOneMessage,
        content: messageRes?.data?.createOneMessage.content
          ? messageRes.data.createOneMessage.content
          : 'No content provided',
        updatedAt: messageRes?.data?.createOneMessage.updatedAt
          ? messageRes.data.createOneMessage.updatedAt
          : 'No updatedAt provided',
      };
      fireEvent(sentFrom, omit(msgData, ['__typename']));
    } else {
      const newThread = await createNewMessageThreadMutation({
        variables: {
          otherUserId: hostId,
        },
      });
      if (!userId || !newThread?.data?.createMessageThread?.id) return;
      const messageRes = await createNewMessage({
        variables: {
          input: {
            message: {
              messageThread: { id: newThread?.data?.createMessageThread?.id },
              user: { id: userId },
              content: message,
            },
          },
        },
        refetchQueries: [
          {
            query: LatestThreadsDocument,
            variables: { userId },
          },
        ],
      });
      const msgData = {
        ...messageRes?.data?.createOneMessage,
        content: messageRes?.data?.createOneMessage.content
          ? messageRes.data.createOneMessage.content
          : 'No content provided',
        updatedAt: messageRes?.data?.createOneMessage.updatedAt
          ? messageRes.data.createOneMessage.updatedAt
          : 'No updatedAt provided',
      };
      fireEvent(sentFrom, omit(msgData, ['__typename']));
    }
    setMessage('');
    onClose();
  };
  return (
    <MainDiv open={open} fullWidth maxWidth="sm">
      <TitleDiv>
        <DialogTitle>Send Message</DialogTitle>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </TitleDiv>
      <DialogContent>
        <TextField
          value={message}
          variant="standard"
          fullWidth
          onChange={e => setMessage(e.target.value)}
        />
        <ButtonDiv>
          <YellowButton onClick={handleSend} style={{ marginTop: '30px' }}>
            Send
          </YellowButton>
        </ButtonDiv>
      </DialogContent>
    </MainDiv>
  );
};

export default MessagePopUp;
